/**
 * Utility to calculate the number of API requests that will be made
 * based on input_files_logic, file count, and numImages
 */
import {
  getMediaTypeFromUrl,
  getSlotMediaTypeFromNameAndMime,
  is3DModelUrl,
} from './mediaTypeDetection';

export type InputFilesLogic = 'single_per_request' | 'all_per_request';

/**
 * Calculate how many API requests will be made to the workflow
 *
 * @param inputFilesLogic - How to batch files in requests
 * @param fileCount - Number of uploaded files
 * @param numImages - Number of images to generate per request
 * @returns Number of API requests that will be made
 */
export function calculateRequestCount(
  inputFilesLogic: InputFilesLogic,
  fileCount: number,
  numImages: number
): number {
  // If no files, use numImages
  if (fileCount === 0) {
    return numImages;
  }

  // If single_per_request, create one request per file
  if (inputFilesLogic === 'single_per_request') {
    return fileCount;
  }

  // If all_per_request, all files go in one request, but may generate multiple images
  return numImages;
}

/**
 * Calculate the expected number of generation slots needed for the gallery
 * This should match the number of API requests that will be made
 *
 * @param workflow - Workflow configuration with file logic settings
 * @param files - Array of uploaded files
 * @param numImages - Number of images to generate per request
 * @param galleryUrlsCount - Number of gallery items selected (already uploaded)
 */
export function calculateExpectedSlots(
  workflow: {
    input_files_logic?: InputFilesLogic;
  },
  files: File[],
  numImages: number,
  galleryUrlsCount: number = 0
): number {
  const inputFilesLogic = workflow.input_files_logic || 'all_per_request';

  // Total media count = uploaded files + gallery items
  const totalMediaCount = files.length + galleryUrlsCount;

  return calculateRequestCount(
    inputFilesLogic,
    totalMediaCount,
    numImages
  );
}

/**
 * Result of media distribution for a single slot
 */
export interface SlotMediaDistribution<T> {
  files: File[];
  galleryUrls: T[];
}

/**
 * Get which files and gallery URLs should be sent for a specific slot index
 * In single_per_request mode, each slot gets exactly ONE media item
 * In all_per_request mode, each slot gets ALL media items
 *
 * @param slotIndex - The index of the slot (0-based)
 * @param inputFilesLogic - How to batch files in requests
 * @param files - Array of uploaded files
 * @param galleryUrls - Array of gallery URL items
 * @returns Object with files and galleryUrls arrays for this slot
 */
export function getMediaForSlot<T>(
  slotIndex: number,
  inputFilesLogic: InputFilesLogic,
  files: File[],
  galleryUrls: T[]
): SlotMediaDistribution<T> {
  const uploadedFilesCount = files.length;
  const galleryUrlsCount = galleryUrls.length;

  if (inputFilesLogic === 'single_per_request') {
    // Each slot processes ONE media item
    if (slotIndex < uploadedFilesCount) {
      // This slot processes an uploaded file
      return {
        files: [files[slotIndex]],
        galleryUrls: []
      };
    } else {
      // This slot processes a gallery URL (offset by uploaded files count)
      const galleryIndex = slotIndex - uploadedFilesCount;
      if (galleryIndex < galleryUrlsCount) {
        return {
          files: [],
          galleryUrls: [galleryUrls[galleryIndex]]
        };
      }
      // Index out of bounds - return empty
      return { files: [], galleryUrls: [] };
    }
  }

  // all_per_request: send all files and gallery URLs together
  return {
    files: [...files],
    galleryUrls: [...galleryUrls]
  };
}

/**
 * Narrow payload custom media keys for single_per_request mode.
 *
 * In single_per_request each request should carry exactly ONE media item,
 * but the widget payload contains ALL blob + gallery URLs across all slots.
 * This function strips blob URLs and gallery URLs that don't belong to this
 * slot's media, so the backend receives only the intended media.
 *
 * Only keys identified as "media keys" (containing blob URLs or gallery URLs)
 * are filtered. Non-media keys (webhook URLs, API endpoints, etc.) pass through
 * untouched.
 *
 * @param payload   - Cloned per-slot payload (mutated in place)
 * @param filesToSend     - Files this slot will upload (0 or 1)
 * @param galleryUrlsToSend - Gallery items this slot will send (0 or 1)
 * @param blobIndexToKeep - Which blob URL (by encounter order) to keep.
 *   In single_per_request the N-th file in requiredFiles corresponds to
 *   the N-th blob URL in the payload (both built in slot iteration order).
 *   Defaults to 0 for backward compatibility.
 * @param allGalleryUrls - All gallery URLs across all slots (used to identify
 *   media keys). When omitted, falls back to galleryUrlsToSend.
 */
export function narrowPayloadForSlot(
  payload: Record<string, any>,
  filesToSend: File[],
  galleryUrlsToSend: { url: string; mediaType?: 'image' | 'video' | 'audio' | 'model' }[],
  blobIndexToKeep: number = 0,
  allGalleryUrls: { url: string; mediaType?: 'image' | 'video' | 'audio' | 'model' }[] = []
): void {
  const counterByMediaType = {
    image: 'slots_image_count',
    video: 'slots_video_count',
    audio: 'slots_audio_count',
    model: 'slots_model_count',
  } as const;
  const slotCounterKeys = Object.values(counterByMediaType);
  const presentSlotCounterKeys = slotCounterKeys.filter(key =>
    Object.prototype.hasOwnProperty.call(payload, key)
  );
  const galleryUrlSet = new Set(galleryUrlsToSend.map(g => g.url));
  const allGalleryUrlSet = new Set(allGalleryUrls.map(g => g.url));
  const hasFileToKeep = filesToSend.length > 0;
  const selectedGalleryUrl = galleryUrlsToSend[0]?.url;
  const selectedGalleryBelongsToPayload = !!selectedGalleryUrl && Object.values(payload).some(value =>
    value === selectedGalleryUrl || (Array.isArray(value) && value.includes(selectedGalleryUrl))
  );
  // Blob-associated files are the Slots widget's named payload values. Standard
  // media widgets contribute files without blob values and use -1, while gallery
  // selections belong to Slots only when their URL is present in the payload.
  const selectedMediaBelongsToSlots = hasFileToKeep
    ? blobIndexToKeep >= 0
    : selectedGalleryBelongsToPayload;

  // Pre-scan: identify media keys (keys containing blob URLs or any gallery URL).
  // Only these keys will have their http/https URLs filtered.
  const mediaKeys = new Set<string>();
  for (const key of Object.keys(payload)) {
    const value = payload[key];
    if (typeof value === 'string') {
      if (value.startsWith('blob:') || allGalleryUrlSet.has(value)) {
        mediaKeys.add(key);
      }
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && (item.startsWith('blob:') || allGalleryUrlSet.has(item))) {
          mediaKeys.add(key);
          break;
        }
      }
    }
  }

  let blobsSeen = 0;

  for (const key of Object.keys(payload)) {
    const value = payload[key];
    const isMediaKey = mediaKeys.has(key);

    if (typeof value === 'string') {
      if (value.startsWith('blob:')) {
        // Blob URLs are always media — filter regardless of key
        if (hasFileToKeep && blobsSeen === blobIndexToKeep) {
          blobsSeen++;
        } else {
          blobsSeen++;
          payload[key] = '';
        }
      } else if (isMediaKey && (value.startsWith('https://') || value.startsWith('http://'))) {
        // HTTP/HTTPS URL in a media key — keep only if it belongs to this slot's gallery
        if (!galleryUrlSet.has(value)) {
          payload[key] = '';
        }
      }
    } else if (Array.isArray(value)) {
      payload[key] = value.filter(item => {
        if (typeof item !== 'string') return true;
        if (item.startsWith('blob:')) {
          if (hasFileToKeep && blobsSeen === blobIndexToKeep) {
            blobsSeen++;
            return true;
          }
          blobsSeen++;
          return false;
        }
        if (isMediaKey && (item.startsWith('https://') || item.startsWith('http://'))) {
          return galleryUrlSet.has(item);
        }
        return true; // Non-URL strings, non-media URLs pass through
      });
    }
  }

  if (presentSlotCounterKeys.length > 0) {
    // Keep every counter emitted by the Slots widget in the narrowed payload.
    // Pricing formulas treat a missing identifier as an error, so deleting a
    // sibling counter (for example audio while processing an image) makes an
    // otherwise valid mixed-media formula fail. Zero means that media type is
    // absent from this one request while preserving the formula's variables.
    presentSlotCounterKeys.forEach(key => { payload[key] = 0; });
    const file = filesToSend[0];
    const selectedMediaType = file
      ? getSlotMediaTypeFromNameAndMime(file.name, file.type)
      : (galleryUrlsToSend[0]?.mediaType
        || (is3DModelUrl(galleryUrlsToSend[0]?.url)
          ? 'model'
          : (galleryUrlsToSend[0]?.url ? getMediaTypeFromUrl(galleryUrlsToSend[0].url) : null)));

    const selectedCounterKey = selectedMediaType
      ? counterByMediaType[selectedMediaType]
      : null;
    if (
      selectedMediaBelongsToSlots
      && selectedCounterKey
      && presentSlotCounterKeys.includes(selectedCounterKey)
    ) {
      payload[selectedCounterKey] = 1;
    }
  }
}
